interface ClsxInput {
  [className: string]: boolean | undefined | null;
}

function clsx(
  ...inputs: (string | ClsxInput | false | null | undefined)[]
): string {
  return inputs
    .filter(Boolean)
    .map((input) => {
      if (typeof input === 'string') return input;
      if (typeof input === 'object' && input !== null) {
        return Object.entries(input)
          .filter(
            ([
              ,
              value,
            ]) => Boolean(value),
          )
          .map(([key]) => key)
          .join(' ');
      }
      return '';
    })
    .join(' ');
}

interface ProgressBarProps {
  value: number; // 0-100
  max?: number;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'accent' | 'success';
  showLabel?: boolean;
  label?: string;
}

export function ProgressBar({
  value,
  max = 100,
  className,
  size = 'md',
  variant = 'primary',
  showLabel = false,
  label,
}: ProgressBarProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  // Determine color based on percentage and variant
  const getProgressColor = () => {
    if (variant === 'success') return 'bg-accent-500';
    if (variant === 'secondary') return 'bg-secondary-500';
    if (variant === 'accent') return 'bg-accent-500';

    // Primary variant with color progression
    if (percentage >= 80) return 'bg-accent-500'; // Green for high progress
    if (percentage >= 50) return 'bg-secondary-400'; // Teal for medium progress
    if (percentage >= 25) return 'bg-primary-400'; // Blue for low progress
    return 'bg-gray-400'; // Gray for very low progress
  };

  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
  };

  return (
    <div className={clsx('w-full', className)}>
      {showLabel && (
        <div className="flex justify-between mb-2">
          <span className="text-sm font-medium text-card-foreground">
            {label}
          </span>
          <span className="text-sm text-muted-foreground">
            {Math.round(percentage)}%
          </span>
        </div>
      )}
      <div
        className={clsx(
          'w-full bg-muted rounded-full overflow-hidden',
          sizeClasses[size],
        )}
      >
        <div
          className={clsx(
            'h-full transition-all duration-300 ease-in-out rounded-full',
            getProgressColor(),
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
